import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AuthStore, FirmwareUpdateStep, FirmwareUpdateStore } from '@store';
import {
  BundleUpdateProcessTypeEnum,
  ComponentVerficationBooleanValue,
  ComponentVerficationTypeValue,
  ComponentVerification,
  VerificationMessage,
} from '@vi-iot/client/index';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BannerType } from 'src/app/shared/components/banner/banner.model';

@Component({
  selector: 'app-firmware-update-verification',
  templateUrl: './firmware-update-verification.component.html',
  styleUrls: ['./firmware-update-verification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FirmwareUpdateVerificationComponent implements OnInit, OnDestroy {
  @Input() gatewaySerial: string;
  @Output() cancel: EventEmitter<void> = new EventEmitter<void>();

  protected readonly bannerType = BannerType;
  protected readonly firmwareupdateProcessType = BundleUpdateProcessTypeEnum;
  protected readonly componentVerficationBooleanValue = ComponentVerficationBooleanValue;
  protected readonly componentVerificationTypeValue = ComponentVerficationTypeValue;

  hasErrorPayload: boolean;
  isEnableOneTimeServiceApprovalButton: boolean;

  messages: VerificationMessage[] = [
    {
      id: this.componentVerificationTypeValue.LEGAL_APPROVED,
      title: '+FIRMWARE_UPDATE.PREPARATION.OWNER_APPROVAL.TITLE',
      description: '+FIRMWARE_UPDATE.PREPARATION.OWNER_APPROVAL.DESCRIPTION',
      errorStatus: this.componentVerficationBooleanValue.NULL,
      status: false,
    },
    {
      id: this.componentVerificationTypeValue.WIFI_SIGNAL_ENOUGH,
      title: '+FIRMWARE_UPDATE.PREPARATION.WIFI_STRENGTH.TITLE',
      description: '+FIRMWARE_UPDATE.PREPARATION.WIFI_STRENGTH.DESCRIPTION',
      errorStatus: this.componentVerficationBooleanValue.NULL,
      status: false,
    },
    {
      id: this.componentVerificationTypeValue.DIAGSTACK_INSTANCES_AVAILABLE,
      title: '+FIRMWARE_UPDATE.PREPARATION.UPDATE_SLOT_AVAILABLE.TITLE',
      description: '+FIRMWARE_UPDATE.PREPARATION.UPDATE_SLOT_AVAILABLE.DESCRIPTION',
      errorStatus: this.componentVerficationBooleanValue.NULL,
      status: false,
    },
  ];

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(public firmwareUpdateStore: FirmwareUpdateStore, public authStore: AuthStore) {}

  ngOnInit(): void {
    this.getVerification();
  }

  openOneTimeServiceApprovalModal(): void {
    this.firmwareUpdateStore.openOneTimeServiceApprovalModal(this.gatewaySerial);
  }

  private getVerification(): void {
    this.firmwareUpdateStore.errorExtendedPayload$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((value: ComponentVerification) => {
        if (value) {
          this.mappingErrorStatus(value);
          this.hasErrorPayload = true;
        } else {
          this.messages.every((item) => {
            item.status = true;
            item.errorStatus = this.componentVerficationBooleanValue.NULL;

            return item;
          });
          this.firmwareUpdateStore.checkOverallProgress();
          this.firmwareUpdateStore.saveLocalStorageFirmwareUpdateState(this.gatewaySerial);
        }
      });
  }

  private mappingErrorStatus(value: ComponentVerification): void {
    const responseMapping = {
      legalApproved: value.legalApproved,
      wifiSignalEnough: value.wifiSignalEnough,
      diagStackInstancesAvailable: value.diagStackInstancesAvailable,
    };

    this.messages = this.messages.map((item: VerificationMessage) => ({
      ...item,
      errorStatus: responseMapping[item.id],
    }));

    const hasWifiSignalEnough = this.messages.some(
      (item) =>
        item.id === this.componentVerificationTypeValue.WIFI_SIGNAL_ENOUGH &&
        item.errorStatus === this.componentVerficationBooleanValue.TRUE
    );

    const hasDiagstackInstancesAvailable = this.messages.some(
      (item) =>
        item.id === this.componentVerificationTypeValue.DIAGSTACK_INSTANCES_AVAILABLE &&
        item.errorStatus === this.componentVerficationBooleanValue.TRUE
    );

    this.isEnableOneTimeServiceApprovalButton =
      hasWifiSignalEnough && hasDiagstackInstancesAvailable;
  }

  onClose(): void {
    this.cancel.emit();
    this.firmwareUpdateStore.clearLocalStorageKey(this.gatewaySerial);
  }

  onContinueCheckingProgress(): void {
    this.firmwareUpdateStore.setStep(FirmwareUpdateStep.LOADING);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.firmwareUpdateStore.clearLocalStorageKey(this.gatewaySerial);
  }
}
